import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { X, UploadSimple, Warning } from "@phosphor-icons/react";
import api from "@/lib/api";

const US_STATES = new Set([
  "AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM",
  "NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA",
  "WV","WI","WY","PR","VI","GU",
]);

/**
 * Bulk clone modal — paste a CSV of `STATE,FEE` rows to onboard a whole
 * national buyer as ONE destination with `state_fees` covering the region.
 *
 * Accepted formats (whitespace tolerant):
 *   AL, 55
 *   GA, 42.50
 *   TN,38
 * A comment line starting with # is skipped. Blank lines are skipped.
 *
 * Everything else (template, digest, campaigns, retry policy) is copied
 * from the source destination. Paused-by-default.
 */
function parseCsv(text) {
  const rows = [];
  const errors = [];
  const seen = new Set();
  const lines = String(text || "").split(/\r?\n/);
  lines.forEach((raw, idx) => {
    const line = raw.trim();
    if (!line || line.startsWith("#")) return;
    const parts = line.split(",").map((s) => s.trim());
    if (parts.length < 2) {
      errors.push(`Line ${idx + 1}: expected 'STATE, FEE'`);
      return;
    }
    const state = parts[0].toUpperCase();
    const fee = parseFloat(parts[1]);
    if (!US_STATES.has(state)) {
      errors.push(`Line ${idx + 1}: '${state}' is not a valid US state`);
      return;
    }
    if (Number.isNaN(fee) || fee < 0) {
      errors.push(`Line ${idx + 1}: fee must be a non-negative number`);
      return;
    }
    if (seen.has(state)) {
      errors.push(`Line ${idx + 1}: duplicate state '${state}'`);
      return;
    }
    seen.add(state);
    rows.push({ state, fee });
  });
  return { rows, errors };
}

export default function BulkCloneModal({ source, onClose, onSaved }) {
  const [csv, setCsv] = useState("AL, 55\nGA, 42\nTN, 38");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [firmName, setFirmName] = useState("");
  const [busy, setBusy] = useState(false);
  const [overlaps, setOverlaps] = useState(null);

  const { rows, errors } = useMemo(() => parseCsv(csv), [csv]);
  const totalFee = useMemo(
    () => rows.reduce((s, r) => s + r.fee, 0),
    [rows]
  );

  const suggestedName = useMemo(() => {
    if (!source) return "";
    return firmName.trim() || `${source.name || "Buyer"} — Region (${rows.length} states)`;
  }, [source, firmName, rows.length]);

  const doSave = async (force) => {
    if (errors.length) return toast.error(errors[0]);
    if (!rows.length) return toast.error("Add at least one STATE, FEE row");
    if (!buyerEmail.includes("@")) return toast.error("Valid buyer email required");
    setBusy(true);
    try {
      const cfg = { ...(source.config || {}) };
      cfg.to = buyerEmail;
      const feeDict = {};
      rows.forEach((r) => { feeDict[r.state] = r.fee; });
      cfg.state_fees = feeDict;
      cfg.state_allowlist = rows.map((r) => r.state);
      if (cfg.daily_digest_enabled) cfg.daily_digest_recipient = buyerEmail;
      delete cfg.daily_digest_last_sent_at;
      delete cfg.reply_to;
      const url = force
        ? "/delivery/destinations?force=true"
        : "/delivery/destinations";
      await api.post(url, {
        client_id: null,
        name: suggestedName,
        type: source.type,
        config: cfg,
        campaign_ids: source.campaign_ids || [],
        is_active: false,
        is_default: false,
        retry_policy: { max_attempts: source.retry_policy?.max_attempts || 5 },
      });
      toast.success(
        `Bulk-cloned ${rows.length} states — paused. Review, then flip Active on.`
      );
      onSaved?.();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === "state_overlap") {
        setOverlaps(detail.overlaps || []);
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Bulk clone failed"));
      }
    } finally {
      setBusy(false);
    }
  };

  const save = (e) => {
    e.preventDefault();
    setOverlaps(null);
    doSave(false);
  };
  const proceedAnyway = () => doSave(true);

  if (!source) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      data-testid="bulk-clone-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <form
        onSubmit={save}
        className="bg-white border-4 border-black w-full max-w-2xl p-6 brut-shadow-sm max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="label-mono">Bulk regions clone</div>
            <h2 className="font-chivo font-black text-2xl uppercase tracking-tighter leading-tight">
              Onboard a whole region at once
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="bulk-clone-close"
            className="border border-black p-1 hover:bg-black hover:text-white"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <p className="text-xs text-zinc-600 mb-4 leading-relaxed">
          Paste a CSV of <code className="bg-zinc-100 px-1 py-0.5">STATE, FEE</code> rows
          (one per line) to create a single destination that covers every state at once.
          Template, digest, campaigns, retry — all copied from{" "}
          <strong>{source.name}</strong>.
        </p>

        <label className="label-mono">Buyer email</label>
        <input
          data-testid="bulk-clone-email"
          type="email"
          value={buyerEmail}
          onChange={(e) => setBuyerEmail(e.target.value)}
          className="w-full border border-black px-3 py-2 mt-1"
          placeholder="ops@nationalbuyer.com"
          autoFocus
        />

        <label className="label-mono mt-3 block">Firm name (optional)</label>
        <input
          data-testid="bulk-clone-name"
          value={firmName}
          onChange={(e) => setFirmName(e.target.value)}
          className="w-full border border-black px-3 py-2 mt-1"
          placeholder={suggestedName}
        />

        <label className="label-mono mt-3 block">States &amp; fees CSV</label>
        <textarea
          data-testid="bulk-clone-csv"
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
          rows={8}
          className="w-full border border-black px-3 py-2 mt-1 font-mono text-sm"
          placeholder="AL, 55&#10;GA, 42.50&#10;TN, 38"
        />

        {/* Live parse feedback */}
        <div className="mt-3 border border-black bg-zinc-50 p-3 text-[11px]">
          <div className="label-mono mb-1">Preview</div>
          <div><strong>States:</strong> {rows.length ? rows.map((r) => r.state).join(", ") : "(none yet)"}</div>
          <div><strong>Total fees (per lead if all states hit):</strong> ${totalFee.toFixed(2)}</div>
          <div><strong>Destination name:</strong> {suggestedName}</div>
          <div><strong>Active on save:</strong> <span className="text-amber-700">no (paused)</span></div>
          {errors.length > 0 && (
            <div className="mt-2 text-rose-700" data-testid="bulk-clone-errors">
              {errors.map((e, i) => <div key={i}>· {e}</div>)}
            </div>
          )}
        </div>

        {overlaps && overlaps.length > 0 && (
          <div
            className="mt-4 border-2 border-amber-700 bg-amber-50 p-3 text-[11px]"
            data-testid="bulk-clone-overlap-warning"
          >
            <div className="flex items-center gap-2 mb-2 text-amber-900 font-bold">
              <Warning size={14} weight="bold" />
              <span className="uppercase tracking-widest text-[10px]">
                Duplicate state guard — {overlaps.length} destination(s) already cover one or more of these states
              </span>
            </div>
            <ul className="space-y-1 text-amber-900 max-h-32 overflow-y-auto">
              {overlaps.map((o) => (
                <li key={o.destination_id} className="border border-amber-700/50 bg-white px-2 py-1">
                  <strong>{o.name}</strong>
                  {o.recipient ? <span className="text-zinc-600"> · {o.recipient}</span> : null}
                  <span className="ml-2 font-mono">[{o.overlapping_states.join(", ")}]</span>
                </li>
              ))}
            </ul>
            <div className="mt-2 text-amber-900">
              Proceeding will fan the same lead to multiple destinations. Confirm you really want that, or narrow your state list.
            </div>
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setOverlaps(null)}
                data-testid="bulk-clone-overlap-cancel"
                className="border border-black px-3 py-1 uppercase text-[10px] tracking-widest hover:bg-zinc-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={proceedAnyway}
                disabled={busy}
                data-testid="bulk-clone-overlap-force"
                className="bg-amber-700 text-white border border-amber-700 px-3 py-1 uppercase text-[10px] tracking-widest font-bold disabled:opacity-50"
              >
                {busy ? "Cloning…" : "Proceed anyway"}
              </button>
            </div>
          </div>
        )}

        {(!overlaps || overlaps.length === 0) && (
          <div className="flex justify-end gap-2 mt-5">
            <button
              type="button"
              onClick={onClose}
              className="border border-black px-4 py-2 uppercase text-xs tracking-widest hover:bg-zinc-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || errors.length > 0 || !rows.length}
              data-testid="bulk-clone-submit"
              className="bg-black text-white border border-black px-4 py-2 uppercase text-xs tracking-widest font-bold disabled:opacity-50"
            >
              <UploadSimple size={12} className="inline mr-1" />
              {busy ? "Creating…" : `Clone ${rows.length} states (paused)`}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
